/**
 * SearchFilters Component - Unified Filter Interface
 * 
 * Provides a clean, single-row filter interface for chord progression search.
 * Features:
 * - Always-visible filter pills with type-specific color coding
 * - Single-row filter controls (Genre, Difficulty, Source, Key, Guitar Count)
 * - Responsive design with proper wrapping
 * - Accessibility compliant with ARIA labels and keyboard navigation
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import './SearchFilters.css';

// Types
import type { SearchOptions } from '../presets/presetSearchAPI';
import type { 
  GenreCategory, 
  DifficultyLevel,
  ProgressionSource
} from '../presets/presetSchema';

// Filter definitions for dropdowns
const GENRE_OPTIONS: GenreCategory[] = [
  'jazz', 'blues', 'rock', 'pop', 'folk', 'practice'
];

const DIFFICULTY_OPTIONS: DifficultyLevel[] = [
  'beginner', 'intermediate', 'advanced'
];

const KEY_OPTIONS = [
  'C Major', 'G Major', 'D Major', 'A Major', 'E Major', 'B Major', 'F# Major',
  'C# Major', 'F Major', 'Bb Major', 'Eb Major', 'Ab Major', 'Db Major',
  'A Minor', 'E Minor', 'B Minor', 'F# Minor', 'C# Minor', 'G# Minor', 'D# Minor',
  'A# Minor', 'D Minor', 'G Minor', 'C Minor', 'F Minor', 'Bb Minor'
];

const GUITAR_COUNT_OPTIONS = ['1-2', '3-4', '5+'];



interface FilterPill {
  id: string;
  type: 'genre' | 'difficulty' | 'key' | 'guitarCount' | 'source' | 'favorite';
  value: string;
  label: string;
  removable: boolean;
}

interface SearchFiltersProps {
  appliedFilters: SearchOptions['filters'];
  detectedFilters: SearchOptions['filters'];
  onFiltersChange: (filters: SearchOptions['filters']) => void;
}

export function SearchFilters({
  appliedFilters = {},
  detectedFilters = {},
  onFiltersChange
}: SearchFiltersProps) {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      const target = event.target as Element;
      // If click is not inside a filter dropdown, close all dropdowns
      if (!target.closest('.filter-dropdown')) {
        setActiveDropdown(null);
      }
    };

    if (activeDropdown) {
      document.addEventListener('click', handleDocumentClick);
      return () => document.removeEventListener('click', handleDocumentClick);
    }
  }, [activeDropdown]);

  // Convert filters to visual pills
  const filterPills = useMemo((): FilterPill[] => {
    const pills: FilterPill[] = [];

    // Helper to add pills for each filter type
    const addPills = (
      filters: SearchOptions['filters'], 
      detected: boolean
    ) => {
      if (!filters) return;

      // Genre pills
      filters.genres?.forEach(genre => {
        pills.push({
          id: `genre-${genre}`,
          type: 'genre',
          value: genre,
          label: genre.charAt(0).toUpperCase() + genre.slice(1),
          removable: !detected
        });
      });

      // Difficulty pills
      filters.difficulties?.forEach(difficulty => {
        pills.push({
          id: `difficulty-${difficulty}`,
          type: 'difficulty',
          value: difficulty,
          label: difficulty.charAt(0).toUpperCase() + difficulty.slice(1),
          removable: !detected
        });
      });

      // Key pills
      filters.keys?.forEach(key => {
        pills.push({
          id: `key-${key}`,
          type: 'key',
          value: key,
          label: key,
          removable: !detected
        });
      });

      // Guitar count pills
      filters.guitarCounts?.forEach(count => {
        pills.push({
          id: `guitarCount-${count}`,
          type: 'guitarCount',
          value: count,
          label: `${count} guitars`,
          removable: !detected
        });
      });

      // Source pills
      filters.sources?.forEach(source => {
        pills.push({
          id: `source-${source}`,
          type: 'source',
          value: source,
          label: source === 'user' ? 'My Progressions' : 'Standard Library',
          removable: !detected
        });
      });

      // Favorite pills
      if (filters.favorites !== undefined) {
        pills.push({
          id: `favorite-${filters.favorites}`,
          type: 'favorite',
          value: filters.favorites.toString(),
          label: filters.favorites ? 'Favorites' : 'Non-Favorites',
          removable: !detected
        });
      }
    };

    // Add detected filters first (non-removable)
    addPills(detectedFilters, true);
    
    // Add manually applied filters (removable)
    addPills(appliedFilters, false);

    // Remove duplicates (prefer detected over manual)
    const uniquePills = pills.filter((pill, index, arr) => 
      arr.findIndex(p => p.type === pill.type && p.value === pill.value) === index
    );

    return uniquePills;
  }, [appliedFilters, detectedFilters]);

  // Handle removing a filter pill
  const handleRemovePill = useCallback((pill: FilterPill) => {
    if (!pill.removable) return;

    const newFilters = { ...appliedFilters };
    
    switch (pill.type) {
      case 'genre':
        newFilters.genres = newFilters.genres?.filter(g => g !== pill.value);
        break;
      case 'difficulty':
        newFilters.difficulties = newFilters.difficulties?.filter(d => d !== pill.value);
        break;
      case 'key':
        newFilters.keys = newFilters.keys?.filter(k => k !== pill.value);
        break;
      case 'guitarCount':
        newFilters.guitarCounts = newFilters.guitarCounts?.filter(gc => gc !== pill.value);
        break;
      case 'source':
        newFilters.sources = newFilters.sources?.filter(s => s !== pill.value);
        break;
      case 'favorite':
        newFilters.favorites = undefined;
        break;
    }

    onFiltersChange(newFilters);
  }, [appliedFilters, onFiltersChange]);

  // Handle dropdown toggle with proper switching behavior
  const handleDropdownToggle = useCallback((dropdownId: string) => {
    // If clicking the same dropdown, close it
    if (activeDropdown === dropdownId) {
      setActiveDropdown(null);
    } else {
      // If clicking a different dropdown, switch to it immediately
      setActiveDropdown(dropdownId);
    }
  }, [activeDropdown]);

  // Handle adding a filter
  const handleAddFilter = useCallback((type: string, value: string) => {
    const newFilters = { ...appliedFilters };

    switch (type) {
      case 'genre':
        newFilters.genres = [...(newFilters.genres || []), value as GenreCategory];
        break;
      case 'difficulty':
        newFilters.difficulties = [...(newFilters.difficulties || []), value as DifficultyLevel];
        break;
      case 'key':
        newFilters.keys = [...(newFilters.keys || []), value];
        break;
      case 'guitarCount':
        newFilters.guitarCounts = [...(newFilters.guitarCounts || []), value];
        break;
      case 'source':
        newFilters.sources = [...(newFilters.sources || []), value as ProgressionSource];
        break;
      case 'favorite':
        newFilters.favorites = value === 'true';
        break;
    }

    onFiltersChange(newFilters);
    setActiveDropdown(null);
  }, [appliedFilters, onFiltersChange]);

  // Handle clearing all manually applied filters
  const handleClearFilters = useCallback(() => {
    onFiltersChange({});
  }, [onFiltersChange]);

  // Handle favorite selection (segmented control)
  const handleFavoriteSelect = useCallback((showFavorites: boolean | null) => {
    const newFilters = { ...appliedFilters };
    if (showFavorites === null) {
      // Show all progressions
      newFilters.favorites = undefined;
    } else {
      // Show only favorites
      newFilters.favorites = showFavorites;
    }
    onFiltersChange(newFilters);
  }, [appliedFilters, onFiltersChange]);

  // Handle source selection (segmented control)
  const handleSourceSelect = useCallback((source: ProgressionSource | null) => {
    const newFilters = { ...appliedFilters };
    if (source === null) {
      // Show all sources
      newFilters.sources = undefined;
    } else {
      // Show only selected source
      newFilters.sources = [source];
    }
    onFiltersChange(newFilters);
  }, [appliedFilters, onFiltersChange]);

  // Check if we have any filters to show
  const hasFilters = filterPills.length > 0;
  const hasManualFilters = filterPills.some(pill => pill.removable);

  return (
    <div className="search-filters">
      {/* Filter Pills Display */}
      {hasFilters && (
        <div className="filter-pills">
          {filterPills.map((pill) => (
            <div 
              key={pill.id} 
              className={`filter-pill ${pill.type} ${pill.removable ? 'removable' : 'detected'}`}
            >
              <span className="pill-label">{pill.label}</span>
              {pill.removable && (
                <button 
                  className="pill-remove"
                  onClick={() => handleRemovePill(pill)}
                  aria-label={`Remove ${pill.label} filter`}
                >
                  ×
                </button>
              )}
            </div>
          ))}
          
          {hasManualFilters && (
            <button 
              className="clear-filters-btn"
              onClick={handleClearFilters}
              title="Clear all manual filters"
            >
              Clear All
            </button>
          )}
        </div>
      )}

      {/* Filter Controls - All filters in one row */}
      <div className="filter-controls">
        <div className="quick-filters">
          {/* Genre Filter Dropdown */}
          <div className="filter-dropdown">
            <button 
              className="filter-dropdown-btn"
              onClick={() => handleDropdownToggle('genre')}
            >
              Genre +
            </button>
            {activeDropdown === 'genre' && (
              <div className="filter-dropdown-menu">
                {GENRE_OPTIONS.filter(genre => 
                  !appliedFilters.genres?.includes(genre) && 
                  !detectedFilters.genres?.includes(genre)
                ).map(genre => (
                  <button
                    key={genre}
                    className="filter-option"
                    onClick={() => handleAddFilter('genre', genre)}
                  >
                    {genre.charAt(0).toUpperCase() + genre.slice(1)}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Difficulty Filter Dropdown */}
          <div className="filter-dropdown">
            <button 
              className="filter-dropdown-btn"
              onClick={() => handleDropdownToggle('difficulty')}
            >
              Difficulty +
            </button>
            {activeDropdown === 'difficulty' && (
              <div className="filter-dropdown-menu">
                {DIFFICULTY_OPTIONS.filter(difficulty => 
                  !appliedFilters.difficulties?.includes(difficulty) && 
                  !detectedFilters.difficulties?.includes(difficulty)
                ).map(difficulty => (
                  <button
                    key={difficulty}
                    className="filter-option"
                    onClick={() => handleAddFilter('difficulty', difficulty)}
                  >
                    {difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Key Filter Dropdown */}
          <div className="filter-dropdown">
            <button 
              className="filter-dropdown-btn"
              onClick={() => handleDropdownToggle('key')}
            >
              Key +
            </button>
            {activeDropdown === 'key' && (
              <div className="filter-dropdown-menu scrollable">
                {KEY_OPTIONS.filter(key => 
                  !appliedFilters.keys?.includes(key) && 
                  !detectedFilters.keys?.includes(key)
                ).map(key => (
                  <button
                    key={key}
                    className="filter-option"
                    onClick={() => handleAddFilter('key', key)}
                  >
                    {key}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Guitar Count Filter Dropdown */}
          <div className="filter-dropdown">
            <button 
              className="filter-dropdown-btn"
              onClick={() => handleDropdownToggle('guitarCount')}
            >
              Count +
            </button>
            {activeDropdown === 'guitarCount' && (
              <div className="filter-dropdown-menu">
                {GUITAR_COUNT_OPTIONS.filter(count => 
                  !appliedFilters.guitarCounts?.includes(count) && 
                  !detectedFilters.guitarCounts?.includes(count)
                ).map(count => (
                  <button
                    key={count}
                    className="filter-option"
                    onClick={() => handleAddFilter('guitarCount', count)}
                  >
                    {count} guitars
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Source Filter - Segmented Control */}
          <div className="filter-segmented-control">
            <button 
              className={`segment ${!appliedFilters.sources ? 'active' : ''}`}
              onClick={() => handleSourceSelect(null)}
              title="Show all progressions"
            >
              All
            </button>
            <button 
              className={`segment ${appliedFilters.sources?.includes('user') ? 'active' : ''}`}
              onClick={() => handleSourceSelect('user')}
              title="Show only my progressions"
            >
              Mine
            </button>
            <button 
              className={`segment ${appliedFilters.sources?.includes('standard') ? 'active' : ''}`}
              onClick={() => handleSourceSelect('standard')}
              title="Show only standard library"
            >
              Library
            </button>
          </div>

          {/* Favorite Filter - Segmented Control */}
          <div className="filter-segmented-control">
            <button 
              className={`segment ${appliedFilters.favorites === undefined ? 'active' : ''}`}
              onClick={() => handleFavoriteSelect(null)}
              title="Show all progressions"
            >
              All
            </button>
            <button 
              className={`segment ${appliedFilters.favorites === true ? 'active' : ''}`}
              onClick={() => handleFavoriteSelect(true)}
              title="Show only favorites"
            >
              <span style={{ fontSize: '12px', lineHeight: '1' }}>⭐</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
